import type { ChangeEvent, FormEvent } from "react";
import { useEffect, useState } from "react";
import type {
  AdminMemberDto,
  AdminMemberInviteDto,
  AdminMemberInvitesResponseDto,
  AdminMembersResponseDto,
  AppMode,
  CreateAdminMemberInviteResponseDto,
  MemberRole,
  MemberStatus
} from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import {
  createAdminMemberInvite,
  createAdminMember,
  fetchAdminMemberInvites,
  fetchAdminMembers,
  patchAdminMember,
  revokeAdminMemberInvite
} from "../../shared/api/adminDirectory";
import {
  getProtectedApiErrorMessage,
  isUnauthenticatedApiError
} from "../../shared/session/protectedApiError";
import { resolveInviteTenantName } from "../../shared/lib/inviteShare";
import { formatTenantDateTime } from "../../shared/lib/tenantDateTime";
import { GENERIC_TENANT_LABELS, getTenantLabels, type TenantLabels } from "../../shared/lib/tenantLabels";
import { describeStatus } from "../../shared/lib/uiLabels";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { InviteSharePanel } from "../../shared/ui/InviteSharePanel";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";

const adminLinks = [
  { to: "/t/:tenantSlug/teacher/admin", label: "管理ホーム" },
  { to: "/t/:tenantSlug/teacher/admin/guardian-links", label: "保護者紐付け" }
];

type MemberFormState = {
  platformUserId: string;
  appNickname: string;
  roles: MemberRole[];
  memberStatus: MemberStatus;
  defaultAppMode: AppMode | null;
  lastUsedAppMode: AppMode | null;
};

type MemberInviteFormState = {
  appNickname: string;
  targetRoles: MemberRole[];
  expiresAt: string;
};

const MEMBER_ROLE_ORDER: MemberRole[] = ["parent", "student", "teacher", "admin"];

function normalizeRoleSelection(roles: readonly MemberRole[]): MemberRole[] {
  const roleSet = new Set<MemberRole>(roles);

  if (roleSet.has("admin")) {
    roleSet.add("teacher");
  }

  return MEMBER_ROLE_ORDER.filter((role) => roleSet.has(role));
}

function createInitialMemberFormState(): MemberFormState {
  return {
    platformUserId: "",
    appNickname: "",
    roles: ["teacher"],
    memberStatus: "active",
    defaultAppMode: "operator",
    lastUsedAppMode: "operator"
  };
}

function createDefaultMemberInviteExpiryInput(now = new Date()): string {
  const next = new Date(now);
  next.setDate(next.getDate() + 14);
  next.setSeconds(0, 0);

  const year = next.getFullYear();
  const month = String(next.getMonth() + 1).padStart(2, "0");
  const day = String(next.getDate()).padStart(2, "0");
  const hour = String(next.getHours()).padStart(2, "0");
  const minute = String(next.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function createInitialMemberInviteFormState(): MemberInviteFormState {
  return {
    appNickname: "",
    targetRoles: ["teacher"],
    expiresAt: createDefaultMemberInviteExpiryInput()
  };
}

function toEditStateMap(members: AdminMembersResponseDto | null): Record<string, MemberFormState> {
  if (!members) {
    return {};
  }

  return Object.fromEntries(
    members.items.map((member) => [
      member.platformUserId,
      {
        platformUserId: member.platformUserId,
        appNickname: member.appNickname ?? "",
        roles: [...member.roles],
        memberStatus: member.memberStatus,
        defaultAppMode: member.defaultAppMode,
        lastUsedAppMode: member.lastUsedAppMode
      }
    ])
  );
}

function buildModeSelectValue(value: AppMode | null): string {
  return value ?? "";
}

function parseModeSelectValue(value: string): AppMode | null {
  return value === "family" || value === "operator" ? value : null;
}

function updateRoles(current: readonly MemberRole[], role: MemberRole, checked: boolean): MemberRole[] {
  const nextRoles = new Set(current);

  if (checked) {
    nextRoles.add(role);
  } else {
    nextRoles.delete(role);
  }

  return normalizeRoleSelection([...nextRoles]);
}

function shouldRecommendAppNickname(roles: readonly MemberRole[]): boolean {
  return roles.includes("teacher") || roles.includes("admin");
}

function getMemberDisplayName(member: AdminMemberDto): string {
  return member.appNickname ?? member.lineDisplayNameSnapshot ?? member.platformUserId;
}

function describeMemberPermissionRole(role: MemberRole, labels: TenantLabels): string {
  switch (role) {
    case "parent":
      return "保護者";
    case "student":
      return labels.student;
    case "teacher":
      return labels.teacher;
    case "admin":
      return "管理者";
  }
}

function describeMemberPermissionRoles(roles: readonly MemberRole[], labels: TenantLabels): string {
  return roles.map((role) => describeMemberPermissionRole(role, labels)).join(" / ");
}

export function AdminMembersPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [members, setMembers] = useState<AdminMembersResponseDto | null>(null);
  const [memberInvites, setMemberInvites] = useState<AdminMemberInvitesResponseDto | null>(null);
  const [memberEdits, setMemberEdits] = useState<Record<string, MemberFormState>>({});
  const [createForm, setCreateForm] = useState<MemberFormState>(createInitialMemberFormState());
  const [inviteForm, setInviteForm] = useState<MemberInviteFormState>(createInitialMemberInviteFormState());
  const [latestIssuedInvite, setLatestIssuedInvite] = useState<CreateAdminMemberInviteResponseDto | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;
  const inviteTenantName = resolveInviteTenantName({
    displayName: session?.tenant.displayName,
    tenantSlug
  });

  async function refreshMembers() {
    setLoadState("loading");
    setLoadError(null);

    try {
      const [nextMembers, nextMemberInvites] = await Promise.all([
        fetchAdminMembers(tenantSlug),
        fetchAdminMemberInvites(tenantSlug)
      ]);
      setMembers(nextMembers);
      setMemberInvites(nextMemberInvites);
      setMemberEdits(toEditStateMap(nextMembers));
      setLoadState("idle");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadState("error");
      setLoadError(getProtectedApiErrorMessage(error, "メンバー一覧の取得に失敗しました"));
    }
  }

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) {
      return;
    }

    void refreshMembers();
  }, [hasAdminRole, isSessionReady, tenantSlug]);

  function updateCreateRole(role: MemberRole, checked: boolean) {
    setCreateForm((current) => ({
      ...current,
      roles: updateRoles(current.roles, role, checked)
    }));
  }

  function updateEditRole(platformUserId: string, role: MemberRole, checked: boolean) {
    setMemberEdits((current) => {
      const target = current[platformUserId];

      if (!target) {
        return current;
      }

      return {
        ...current,
        [platformUserId]: {
          ...target,
          roles: updateRoles(target.roles, role, checked)
        }
      };
    });
  }

  function updateInviteRole(role: MemberRole, checked: boolean) {
    setInviteForm((current) => ({
      ...current,
      targetRoles: updateRoles(current.targetRoles, role, checked).filter(
        (nextRole) => nextRole === "teacher" || nextRole === "admin"
      )
    }));
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyKey("create");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      await createAdminMember(tenantSlug, {
        platformUserId: createForm.platformUserId.trim(),
        appNickname: createForm.appNickname.trim() || null,
        roles: normalizeRoleSelection(createForm.roles),
        memberStatus: createForm.memberStatus,
        defaultAppMode: createForm.defaultAppMode,
        lastUsedAppMode: createForm.lastUsedAppMode
      });
      setCreateForm(createInitialMemberFormState());
      await refreshMembers();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "メンバー作成に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleCreateInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyKey("create-invite");
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const created = await createAdminMemberInvite(tenantSlug, {
        appNickname: inviteForm.appNickname.trim() || null,
        targetRoles: normalizeRoleSelection(inviteForm.targetRoles).filter(
          (role) => role === "teacher" || role === "admin"
        ),
        expiresAt: inviteForm.expiresAt ? new Date(inviteForm.expiresAt).toISOString() : null,
        maxUseCount: null
      });
      setLatestIssuedInvite(created);
      setInviteForm(createInitialMemberInviteFormState());
      setFeedbackMessage(`招待を作成しました: ${created.invite.inviteId}`);
      await refreshMembers();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "招待の作成に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleRevokeInvite(inviteId: string) {
    setBusyKey(`revoke-invite:${inviteId}`);
    setLoadError(null);
    setFeedbackMessage(null);

    try {
      const revoked = await revokeAdminMemberInvite(tenantSlug, inviteId);
      setFeedbackMessage(`招待を失効しました: ${revoked.inviteId}`);
      await refreshMembers();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "招待の失効に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  async function handleUpdate(platformUserId: string) {
    const editState = memberEdits[platformUserId];

    if (!editState) {
      return;
    }

    setBusyKey(`update:${platformUserId}`);
    setLoadError(null);

    try {
      await patchAdminMember(tenantSlug, platformUserId, {
        appNickname: editState.appNickname.trim() || null,
        roles: normalizeRoleSelection(editState.roles),
        memberStatus: editState.memberStatus,
        defaultAppMode: editState.defaultAppMode,
        lastUsedAppMode: editState.lastUsedAppMode
      });
      await refreshMembers();
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, "メンバー更新に失敗しました"));
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title={`メンバー / ${tenantLabels.teacher}`}
      description={`${tenantLabels.teacher}・管理者の招待、メンバー権限、表示名を管理します。`}
      links={adminLinks}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>メンバー一覧を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}

        {isSessionReady && hasAdminRole ? (
          <>
            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>{tenantLabels.teacher} / 管理者を招待する</h2>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                本番の基本フローは招待リンクです。管理者が LINE sub を集めたり入力したりする必要はありません。
              </p>
              <form
                onSubmit={(event) => void handleCreateInvite(event)}
                style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
              >
                <div style={{ display: "grid", gap: 6 }}>
                  <strong style={{ fontSize: 14 }}>招待する権限</strong>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {(["teacher", "admin"] as const).map((role) => (
                      <label key={role} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input
                          type="checkbox"
                          checked={inviteForm.targetRoles.includes(role)}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => updateInviteRole(role, event.target.checked)}
                        />
                        <span>
                          {role === "teacher" ? `${tenantLabels.teacher}を招待` : "管理者を招待"}
                        </span>
                      </label>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    管理者招待には{tenantLabels.teacher}権限も自動で含まれます。
                  </div>
                </div>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>表示名</span>
                  <input
                    value={inviteForm.appNickname}
                    onChange={(event) =>
                      setInviteForm((current) => ({ ...current, appNickname: event.target.value }))
                    }
                    placeholder="Mika / 田中先生"
                  />
                  <span style={{ fontSize: 12, opacity: 0.8 }}>
                      {tenantLabels.teacher}名や管理画面上の表示名として使われます。
                  </span>
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>有効期限</span>
                  <input
                    type="datetime-local"
                    value={inviteForm.expiresAt}
                    onChange={(event) =>
                      setInviteForm((current) => ({ ...current, expiresAt: event.target.value }))
                    }
                  />
                </label>

                <button type="submit" disabled={busyKey === "create-invite"}>
                  {busyKey === "create-invite" ? "作成中..." : "招待を作成する"}
                </button>
              </form>
            </section>

            {latestIssuedInvite ? (
              <InviteSharePanel
                tenantName={inviteTenantName}
                inviteKind={latestIssuedInvite.invite.targetRoles.includes("admin") ? "memberAdmin" : "memberTeacher"}
                primaryUrl={latestIssuedInvite.delivery.inviteUrl}
                inviteId={latestIssuedInvite.invite.inviteId}
                onRevoke={() => void handleRevokeInvite(latestIssuedInvite.invite.inviteId)}
                revokeBusy={busyKey === `revoke-invite:${latestIssuedInvite.invite.inviteId}`}
              />
            ) : null}

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>招待一覧</h2>
              {memberInvites?.items.length === 0 ? <p style={{ margin: 0 }}>招待はまだありません。</p> : null}
              <div style={{ display: "grid", gap: 12 }}>
                {memberInvites?.items.map((invite) => (
                  <article
                    key={invite.inviteId}
                    style={{ border: "1px solid #e5e5e5", borderRadius: 12, padding: 12, display: "grid", gap: 6 }}
                  >
                    <strong>{describeMemberPermissionRoles(invite.targetRoles, tenantLabels)}</strong>
                    <div style={{ fontSize: 14, opacity: 0.8 }}>
                      {describeStatus(invite.status)} / 有効期限: {formatTenantDateTime(invite.expiresAt)}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.8 }}>
                      表示名: {invite.appNickname ?? "未設定"} / 使用回数: {invite.usedCount} / {invite.maxUseCount}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.7 }}>{invite.inviteId}</div>
                    <button
                      type="button"
                      onClick={() => void handleRevokeInvite(invite.inviteId)}
                      disabled={busyKey === `revoke-invite:${invite.inviteId}` || invite.status !== "active"}
                    >
                      {busyKey === `revoke-invite:${invite.inviteId}` ? "失効中..." : "招待を失効する"}
                    </button>
                  </article>
                ))}
              </div>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>レビュー用: 直接メンバーを追加する</h2>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                本番の基本運用ではありません。レビュー環境では既存 platformUserId を指定してメンバーを追加できます。LINE sub は不要です。
              </p>
              <form
                onSubmit={(event) => void handleCreate(event)}
                style={{ display: "grid", gap: 12, padding: 12, border: "1px solid #e5e5e5", borderRadius: 12 }}
              >
                <label style={{ display: "grid", gap: 4 }}>
                  <span>platformUserId</span>
                  <input
                    value={createForm.platformUserId}
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, platformUserId: event.target.value }))
                    }
                    placeholder="platform_user_teacher_candidate_001"
                  />
                </label>

                <label style={{ display: "grid", gap: 4 }}>
                  <span>表示名</span>
                  <input
                    value={createForm.appNickname}
                    onChange={(event) =>
                      setCreateForm((current) => ({ ...current, appNickname: event.target.value }))
                    }
                    placeholder="Mika / Review Owner"
                  />
                  {shouldRecommendAppNickname(createForm.roles) ? (
                    <span style={{ fontSize: 12, opacity: 0.8 }}>
                      {tenantLabels.teacher}名や管理画面上の表示名として使われます。
                    </span>
                  ) : null}
                </label>

                <div style={{ display: "grid", gap: 6 }}>
                  <strong style={{ fontSize: 14 }}>権限</strong>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {MEMBER_ROLE_ORDER.map((role) => (
                      <label key={role} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input
                          type="checkbox"
                          checked={createForm.roles.includes(role)}
                          onChange={(event: ChangeEvent<HTMLInputElement>) => updateCreateRole(role, event.target.checked)}
                        />
                        <span>{describeMemberPermissionRole(role, tenantLabels)}</span>
                      </label>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    roles は権限です。active の member は少なくとも 1 つの role が必要です。admin を選ぶと teacher role も自動で保持します。
                  </div>
                </div>

                <div style={{ display: "grid", gap: 8 }}>
                  <label style={{ display: "grid", gap: 4 }}>
                    <span>メンバー状態</span>
                    <select
                      value={createForm.memberStatus}
                      onChange={(event) =>
                        setCreateForm((current) => ({
                          ...current,
                          memberStatus: event.target.value === "inactive" ? "inactive" : "active"
                        }))
                      }
                    >
                      <option value="active">有効</option>
                      <option value="inactive">無効</option>
                    </select>
                    <span style={{ fontSize: 12, opacity: 0.8 }}>
                      利用停止する場合は memberStatus を inactive にしてください。roles を空にする場合も inactive が必要です。
                    </span>
                  </label>

                  <label style={{ display: "grid", gap: 4 }}>
                    <span>初期モード</span>
                    <select
                      value={buildModeSelectValue(createForm.defaultAppMode)}
                      onChange={(event) =>
                        setCreateForm((current) => ({
                          ...current,
                          defaultAppMode: parseModeSelectValue(event.target.value)
                        }))
                      }
                    >
                      <option value="">未設定</option>
                      <option value="family">ホーム</option>
                      <option value="operator">運営モード</option>
                    </select>
                  </label>

                  <label style={{ display: "grid", gap: 4 }}>
                    <span>前回利用モード</span>
                    <select
                      value={buildModeSelectValue(createForm.lastUsedAppMode)}
                      onChange={(event) =>
                        setCreateForm((current) => ({
                          ...current,
                          lastUsedAppMode: parseModeSelectValue(event.target.value)
                        }))
                      }
                    >
                      <option value="">未設定</option>
                      <option value="family">ホーム</option>
                      <option value="operator">運営モード</option>
                    </select>
                  </label>
                </div>

                <button type="submit" disabled={busyKey === "create"}>
                  {busyKey === "create" ? "作成中..." : "メンバーを作成する"}
                </button>
              </form>
            </section>

            <section style={{ display: "grid", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>メンバー一覧</h2>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.8 }}>
                platformUser 自体の新規作成 API はまだありません。レビューでは既存 platformUserId を指定してメンバーを追加します。
              </p>

              {members?.items.length === 0 ? <p style={{ margin: 0 }}>メンバーはありません。</p> : null}
              {members?.items.map((member) => {
                const editState = memberEdits[member.platformUserId];

                if (!editState) {
                  return null;
                }

                return (
                  <article
                    key={member.platformUserId}
                    style={{
                      border: "1px solid #e5e5e5",
                      borderRadius: 12,
                      padding: 12,
                      display: "grid",
                      gap: 10
                    }}
                  >
                    <div style={{ display: "grid", gap: 4 }}>
                      <strong>{getMemberDisplayName(member)}</strong>
                      <span style={{ fontSize: 12, opacity: 0.8 }}>{member.platformUserId}</span>
                      {member.appNickname && member.lineDisplayNameSnapshot ? (
                        <span style={{ fontSize: 12, opacity: 0.8 }}>
                          LINE snapshot: {member.lineDisplayNameSnapshot}
                        </span>
                      ) : null}
                    </div>

                    <label style={{ display: "grid", gap: 4 }}>
                      <span>表示名</span>
                      <input
                        value={editState.appNickname}
                        onChange={(event) =>
                          setMemberEdits((current) => ({
                            ...current,
                            [member.platformUserId]: {
                              ...editState,
                              appNickname: event.target.value
                            }
                          }))
                        }
                        placeholder="Mika / Review Owner"
                      />
                      {shouldRecommendAppNickname(editState.roles) ? (
                        <span style={{ fontSize: 12, opacity: 0.8 }}>
                          {tenantLabels.teacher}名や管理画面上の表示名として使われます。
                        </span>
                      ) : null}
                    </label>

                    <div style={{ display: "grid", gap: 6 }}>
                      <strong style={{ fontSize: 14 }}>権限</strong>
                      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                        {MEMBER_ROLE_ORDER.map((role) => (
                          <label key={role} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <input
                              type="checkbox"
                              checked={editState.roles.includes(role)}
                              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                updateEditRole(member.platformUserId, role, event.target.checked)
                              }
                            />
                            <span>{describeMemberPermissionRole(role, tenantLabels)}</span>
                          </label>
                        ))}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.8 }}>
                        有効なメンバーには少なくとも 1 つの権限が必要です。利用停止する場合はメンバー状態を無効にしてください。
                      </div>
                    </div>

                    <div style={{ display: "grid", gap: 8 }}>
                      <label style={{ display: "grid", gap: 4 }}>
                        <span>メンバー状態</span>
                        <select
                          value={editState.memberStatus}
                          onChange={(event) =>
                            setMemberEdits((current) => ({
                              ...current,
                              [member.platformUserId]: {
                                ...editState,
                                memberStatus: event.target.value === "inactive" ? "inactive" : "active"
                              }
                            }))
                          }
                        >
                          <option value="active">有効</option>
                          <option value="inactive">無効</option>
                        </select>
                      <span style={{ fontSize: 12, opacity: 0.8 }}>
                        無効にすると `session/me` と保護 API で現在の権限が再評価され、利用停止として扱われます。
                      </span>
                      </label>

                      <label style={{ display: "grid", gap: 4 }}>
                        <span>初期モード</span>
                        <select
                          value={buildModeSelectValue(editState.defaultAppMode)}
                          onChange={(event) =>
                            setMemberEdits((current) => ({
                              ...current,
                              [member.platformUserId]: {
                                ...editState,
                                defaultAppMode: parseModeSelectValue(event.target.value)
                              }
                            }))
                          }
                        >
                          <option value="">未設定</option>
                          <option value="family">ホーム</option>
                          <option value="operator">運営モード</option>
                        </select>
                      </label>

                      <label style={{ display: "grid", gap: 4 }}>
                        <span>前回利用モード</span>
                        <select
                          value={buildModeSelectValue(editState.lastUsedAppMode)}
                          onChange={(event) =>
                            setMemberEdits((current) => ({
                              ...current,
                              [member.platformUserId]: {
                                ...editState,
                                lastUsedAppMode: parseModeSelectValue(event.target.value)
                              }
                            }))
                          }
                        >
                          <option value="">未設定</option>
                          <option value="family">ホーム</option>
                          <option value="operator">運営モード</option>
                        </select>
                      </label>
                    </div>

                    <div style={{ display: "grid", gap: 4, fontSize: 12, opacity: 0.8 }}>
                      <span>作成日時: {formatTenantDateTime(member.createdAt)}</span>
                      <span>更新日時: {formatTenantDateTime(member.updatedAt)}</span>
                      <span>最終アクセス: {member.lastAccessAt ? formatTenantDateTime(member.lastAccessAt) : "未設定"}</span>
                    </div>

                    <button
                      type="button"
                      onClick={() => void handleUpdate(member.platformUserId)}
                      disabled={busyKey === `update:${member.platformUserId}`}
                    >
                      {busyKey === `update:${member.platformUserId}` ? "更新中..." : "メンバーを更新する"}
                    </button>
                  </article>
                );
              })}
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
